import React, { useState } from 'react';
import OnlinePlayersLobby from './OnlinePlayersLobby';
import MultiplayerLobbyKBC from './MultiplayerLobbyKBC';
import TeacherQuestionSelector from './TeacherQuestionSelector';
import './MultiplayerLobby.css';

const MultiplayerLobby = ({ userRole, userClass, userName, userEmail, userId, onStartGame, onExit }) => {
  const [showOnlinePlayers, setShowOnlinePlayers] = useState(false);
  const [showKBCLobby, setShowKBCLobby] = useState(false);
  const [showMultiplexSelector, setShowMultiplexSelector] = useState(false);
  const [gameConfig, setGameConfig] = useState({
    subject: '',
    class: userClass || '1',
    difficulty: 'easy',
    source: 'both',
    questionPaper: ''
  });
  const [availableOptions, setAvailableOptions] = useState({ subjects: [], classes: [] });
  const [questionPapers, setQuestionPapers] = useState([]);
  const [showQuestionPapers, setShowQuestionPapers] = useState(false);

  // Fetch available options
  React.useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch('/api/quiz/available-options');
        if (response.ok) {
          const options = await response.json();
          setAvailableOptions(options);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    fetchOptions();
  }, []);

  // Fetch question papers when subject/class changes
  React.useEffect(() => {
    if (gameConfig.subject && gameConfig.class) {
      const fetchPapers = async () => {
        try {
          const params = new URLSearchParams({
            subject: gameConfig.subject,
            class: gameConfig.class
          });
          const response = await fetch(`/api/quiz/question-papers?${params}`);
          if (response.ok) {
            const papers = await response.json();
            setQuestionPapers(papers);
          }
        } catch (error) {
          console.error('Error fetching question papers:', error);
        }
      };
      fetchPapers();
    }
  }, [gameConfig.subject, gameConfig.class]);

  const startSinglePlayer = () => {
    onStartGame({
      mode: 'single',
      config: gameConfig,
      players: [{ name: userName, role: userRole }]
    });
  };

  if (showOnlinePlayers) {
    return (
      <OnlinePlayersLobby 
        userRole={userRole} 
        userName={userName} 
        userClass={userClass}
        userEmail={userEmail}
        userId={userId}
        onStartGame={onStartGame}
        onExit={() => setShowOnlinePlayers(false)} 
      />
    );
  }

  if (showKBCLobby) {
    return (
      <MultiplayerLobbyKBC 
        userRole={userRole} 
        userName={userName} 
        userClass={userClass}
        userEmail={userEmail}
        userId={userId}
        onStartGame={onStartGame}
        onExit={() => setShowKBCLobby(false)} 
      />
    );
  }

  if (showMultiplexSelector) {
    return (
      <TeacherQuestionSelector 
        userRole={userRole} 
        userName={userName}
        onStartGame={onStartGame}
        onBack={() => setShowMultiplexSelector(false)} 
      />
    );
  }

  return (
    <div className="multiplayer-lobby">
      <div className="lobby-container">
        <h2>Quiz Game</h2>
        
        <div className="game-mode-selection">
          <div className="mode-buttons">
            <button 
              className="mode-btn active"
              onClick={startSinglePlayer}
              disabled={!gameConfig.subject || (showQuestionPapers && !gameConfig.questionPaper)}
            >
              <span className="mode-icon">👤</span>
              <span>Single Player</span>
              <small>Play alone and test your knowledge</small>
            </button>
            
            <button 
              className="mode-btn"
              onClick={() => setShowKBCLobby(true)}
            >
              <span className="mode-icon">🏆</span>
              <span>Multiplayer KBC</span>
              <small>Challenge others in KBC style game</small>
            </button>
            
            {['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(userRole) && (
              <button 
                className="mode-btn"
                onClick={() => setShowMultiplexSelector(true)}
                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)', color: 'white' }}
              >
                <span className="mode-icon">🎯</span>
                <span>KBC Multiplex Mode</span>
                <small>Custom 12 questions • No time limit • Teacher only</small>
              </button>
            )}
            
            <button 
              className="mode-btn"
              onClick={() => setShowOnlinePlayers(true)}
            >
              <span className="mode-icon">🌐</span>
              <span>Quick Match</span>
              <small>Quick online challenges</small>
            </button>
          </div>
        </div>

        <div className="game-config">
          <h3>Game Settings</h3>
          <div className="config-grid">
            <div className="config-item">
              <label>Subject:</label>
              <select 
                value={gameConfig.subject} 
                onChange={(e) => setGameConfig({...gameConfig, subject: e.target.value, questionPaper: ''})}
              >
                <option value="">Select Subject</option>
                {availableOptions.subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            
            <div className="config-item">
              <label>Class:</label>
              <select 
                value={gameConfig.class} 
                onChange={(e) => setGameConfig({...gameConfig, class: e.target.value, questionPaper: ''})}
              >
                {availableOptions.classes.map(cls => (
                  <option key={cls} value={cls}>{cls.toString().startsWith('Class') ? cls : `Class ${cls}`}</option>
                ))}
              </select>
            </div>
            
            <div className="config-item">
              <label>Question Source:</label>
              <select 
                value={gameConfig.source} 
                onChange={(e) => {
                  setGameConfig({...gameConfig, source: e.target.value, questionPaper: ''});
                  setShowQuestionPapers(e.target.value === 'papers');
                }}
              >
                <option value="both">📚 All Questions</option>
                <option value="papers">📋 Question Papers</option>
                <option value="quiz">🎯 Quiz Bank</option>
              </select>
            </div>
            
            {showQuestionPapers && questionPapers.length > 0 && (
              <div className="config-item full-width">
                <label>Select Question Paper:</label>
                <select 
                  value={gameConfig.questionPaper} 
                  onChange={(e) => setGameConfig({...gameConfig, questionPaper: e.target.value})}
                >
                  <option value="">Choose a Question Paper</option>
                  {questionPapers.map(paper => (
                    <option key={paper._id} value={paper._id}>
                      {paper.title} ({paper.totalQuestions} questions)
                      {paper.chapter && ` - ${paper.chapter}`}
                      {paper.topic && ` - ${paper.topic}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="config-item">
              <label>Difficulty:</label>
              <select 
                value={gameConfig.difficulty} 
                onChange={(e) => setGameConfig({...gameConfig, difficulty: e.target.value})}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
          
          {questionPapers.length > 0 && gameConfig.subject && gameConfig.class && (
            <div style={{ marginTop: '15px', padding: '10px', background: '#e8f5e8', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>📋 Available Question Papers ({questionPapers.length})</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
                {questionPapers.slice(0, 3).map(paper => (
                  <div key={paper._id} style={{ padding: '8px', background: 'white', borderRadius: '6px', border: '1px solid #4caf50' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>{paper.title}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {paper.totalQuestions} questions
                      {paper.chapter && ` • ${paper.chapter}`}
                    </div>
                  </div>
                ))}
                {questionPapers.length > 3 && (
                  <div style={{ padding: '8px', background: '#f0f0f0', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#666' }}>
                    +{questionPapers.length - 3} more papers
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button className="exit-btn" onClick={onExit}>
            Back to Games
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiplayerLobby;